import { Job, Property, PropertyListItem, Rule, Ruleset } from 'types';
import { getRequestHeaders, handleRes, logError } from 'utils';

export interface GetPropertyResponse extends Response {
  property?: Property;
}

export interface SendPermitInviteResponse extends Response {
  ID?: string;
}
export interface GetPropertyJobsResponse extends Response {
  jobs?: Job[];
}

export interface CreatePropertyParams {
  body: string; // CreatePropertyBody JSON string
  tenantID: string | number | undefined;
}
export const createProperty = async ({
  body,
  tenantID,
}: CreatePropertyParams): Promise<GetPropertyResponse> => {
  return await fetch(
    `${window.PROPERTY_BASE_URL}/tenant/${tenantID}/properties`,
    {
      method: 'POST',
      ...getRequestHeaders(),
      body,
    },
  );
};

export interface UpdatePropertyParams {
  tenantID: string | number | undefined;
  propertyID: string | number | undefined;
  body: string; // PutPropertyBody string
}
export const updateProperty = async ({
  tenantID,
  propertyID,
  body,
}: UpdatePropertyParams): Promise<GetPropertyResponse> => {
  return await fetch(
    `${window.PROPERTY_BASE_URL}/tenant/${tenantID}/properties/${propertyID}`,
    {
      method: 'PATCH',
      ...getRequestHeaders(),
      body: body,
    },
  );
};

// interface GetPropertyJobsParams {
//   tenantID: string | number | undefined;
//   propertyID: string | number | undefined;
//   body: string; // GetPropertyJobsBody
// }
// export const getPropertyJobs = async ({
//   tenantID,
//   propertyID,
//   body,
// }: GetPropertyJobsParams): Promise<GetPropertyJobsResponse> => {
//   return fetch(
//     `${window.PROPERTY_BASE_URL}/tenant/${tenantID}/properties/${propertyID}/jobs`,
//     {
//       method: 'POST',
//       ...getRequestHeaders(),
//       body,
//     },
//   );
// };

interface GetPropertyRulesParams {
  tenantID: string | number | undefined;
  propertyID: string | number | undefined;
}
interface GetPropertyRulesOptions extends GetPropertyRulesParams {
  includeInactive?: boolean;
}
export const getPropertyRules = ({
  tenantID,
  propertyID,
  includeInactive,
}: GetPropertyRulesOptions): Promise<Rule[]> => {
  const params = includeInactive ? '?includeInactive=true' : '';
  return fetch(
    `${window.PROPERTY_BASE_URL}/tenant/${tenantID}/properties/${propertyID}/rules${params}`,
    {
      method: 'GET',
      ...getRequestHeaders(),
    },
  )
    .then((res) => handleRes<Rule[]>(res, 200))
    .catch((e) => {
      throw e;
    });
};

interface GetPropertyRulesetsParams {
  tenantID: string | number | undefined;
  propertyID: string | number | undefined;
}
export const getPropertyRulesets = ({
  tenantID,
  propertyID,
}: GetPropertyRulesetsParams): Promise<Ruleset[]> => {
  return fetch(
    `${window.PROPERTY_BASE_URL}/tenant/${tenantID}/properties/${propertyID}/rulesets`,
    {
      method: 'GET',
      ...getRequestHeaders(),
    },
  )
    .then((res) => handleRes<Ruleset[]>(res, 200))
    .catch((e) => {
      throw e;
    });
};

export const getTenantProperties = (tenantID: number): Promise<Property[]> => {
  return fetch(`${window.PROPERTY_BASE_URL}/tenant/${tenantID}/properties`, {
    method: 'GET',
    ...getRequestHeaders(),
  })
    .then((res) => handleRes<Property[]>(res, 200))
    .catch((e) => {
      logError(e);
      return [];
    });
};

export const getTenantPropertiesList = (
  tenantID: number,
): Promise<PropertyListItem[]> => {
  return fetch(
    `${window.PROPERTY_BASE_URL}/tenant/${tenantID}/properties/list`,
    {
      method: 'GET',
      ...getRequestHeaders(),
    },
  )
    .then((res) => handleRes<PropertyListItem[]>(res, 200))
    .catch((e) => {
      logError(e);
      return [];
    });
};

interface GetPropertyByIDParams {
  tenantID: string | number | undefined;
  propertyID: string | number | undefined;
}
export const getPropertyByID = ({
  tenantID,
  propertyID,
}: GetPropertyByIDParams): Promise<Property> => {
  return fetch(
    `${window.PROPERTY_BASE_URL}/tenant/${tenantID}/properties/${propertyID}`,
    {
      method: 'GET',
      ...getRequestHeaders(),
    },
  )
    .then((res) => handleRes<Property>(res, 200))
    .catch((e) => {
      throw e;
    });
};

export interface PutPropertyGroupPropertiesParams {
  tenantID: string | number | undefined;
  groupID: string | number | undefined;
  body: string; // JSON string of Property[]
}
export const putPropertyGroupProperties = async ({
  tenantID,
  groupID,
  body,
}: PutPropertyGroupPropertiesParams): Promise<Response> => {
  return await fetch(
    `${window.PROPERTY_BASE_URL}/tenant/${tenantID}/propertyGroups/${groupID}/properties`,
    {
      method: 'PUT',
      ...getRequestHeaders(),
      body,
    },
  );
};

export interface PatchPropertyParams {
  tenantID: string | number | undefined;
  propertyID: string | number | undefined;
  body: string; // PropertyUpdateBody
}
export const patchProperty = async ({
  tenantID,
  propertyID,
  body,
}: PatchPropertyParams): Promise<Response> => {
  return await fetch(
    `${window.PROPERTY_BASE_URL}/tenant/${tenantID}/property/${propertyID}`,
    {
      method: 'PATCH',
      ...getRequestHeaders(),
      body,
    },
  );
};

export interface ImportPropertiesParams {
  tenantID: string | number | undefined;
  systemID: string | number | undefined;
  body: string; // ImportPropertiesBody
}
export const importProperties = async ({
  tenantID,
  systemID,
  body,
}: ImportPropertiesParams): Promise<Response> => {
  return await fetch(
    `${window.PROPERTY_BASE_URL}/system/${systemID}/tenant/${tenantID}/property-import`,
    {
      method: 'POST',
      ...getRequestHeaders(),
      body,
    },
  );
};
